import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TeamApi, type Reply } from "./teamApi";
import { useTeam, useT } from "./TeamUi";

// Lo que OperatingComponentBase daba a cada pantalla de Equipo (Busy, Error, RunAsync y Accept), el estado de la lista en
// la URL y el scroll (OperatingListState) y el ancho del celular (TeamInterop.isMobile/watchMobile).

/** Código de falla del servidor → texto (OperatingComponentBase.Accept). */
export function failureKey(code: string): string {
  switch (code) {
    case "FORBIDDEN":
    case "RELEASE_SCOPE_DENIED":
    case "SELF_ESCALATION":
      return "CFG1_FORBIDDEN";
    case "STALE_VERSION":
    case "STALE_RELEASE_IMPACT":
    case "IDEMPOTENCY_CONFLICT":
      return "CFG1_VERSION_CONFLICT";
    case "OVERLAPPING_ROLES":
      return "CFG1_ROLE_OVERLAP";
    case "NAME_OR_PARTICIPATION_CONFLICT":
      return "CFG1_UNIQUE_NAME";
    case "SELECTION_NO_LONGER_AUTHORIZED":
      return "CFG1_SELECTION_INVALID";
    default:
      return "CFG1_ERROR";
  }
}

export function useApi(): TeamApi {
  const ui = useTeam();
  return useMemo(() => new TeamApi(ui.organizationId), [ui.organizationId]);
}

/** Busy, Error, Run y Accept: un trabajo a la vez; un fallo de red deja «No se pudo…» y nunca borra el borrador. */
export function useOperation() {
  const T = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const working = useRef(false);
  const run = useCallback(
    async (operation: () => Promise<void>) => {
      if (working.current) return;
      working.current = true;
      setBusy(true);
      setError(null);
      try {
        await operation();
      } catch {
        setError(T("CFG1_ERROR"));
      } finally {
        working.current = false;
        setBusy(false);
      }
    },
    [T],
  );
  const accept = useCallback(
    <V>(reply: Reply<V>): reply is Reply<V> & { value: V } => {
      if (reply.failureCode === null) return true;
      setError(T(failureKey(reply.failureCode)));
      return false;
    },
    [T],
  );
  return { busy, error, setError, run, accept, working };
}

/** El celular (C17): en el celular la lista carga sola; en escritorio, paginado. */
export function useMobile(): boolean {
  const [mobile, setMobile] = useState(() => matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const query = matchMedia("(max-width: 767px)");
    const listener = (event: MediaQueryListEvent) => setMobile(event.matches);
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);
  return mobile;
}

/**
 * OperatingListState: búsqueda, página y filtros en la URL (sin navegar ni llenar el historial) y el scroll de la página
 * recordado por dirección en sessionStorage. Solo se guarda la consulta y el scroll; nada protegido.
 */
export function useListState(
  enabled: boolean,
  search: string,
  page: number,
  filters?: Record<string, string>,
  status = "",
  role = "",
) {
  const ui = useTeam();
  const binding = useRef<{ main: HTMLElement | null; key: string; remember: () => void } | null>(null);
  const route = useRef(location.pathname);
  const serialized = JSON.stringify(filters ?? {});
  useEffect(() => {
    if (!enabled || location.pathname.toLowerCase() !== route.current.toLowerCase()) return;
    const url = new URL(location.href);
    url.searchParams.set("q", search);
    url.searchParams.set("p", String(page));
    if (status) url.searchParams.set("status", status);
    else url.searchParams.delete("status");
    if (role) url.searchParams.set("role", role);
    else url.searchParams.delete("role");
    for (const [key, value] of Object.entries(JSON.parse(serialized) as Record<string, string>)) {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    const key = `cfg1:${ui.organizationId}:${ui.userId}${url.pathname}${url.search}`;
    if (!binding.current) {
      const main = document.querySelector<HTMLElement>("main");
      const state = { main, key, remember: () => undefined as void };
      state.remember = () => {
        try {
          sessionStorage.setItem(state.key, String(main?.scrollTop ?? 0));
        } catch {
          /* sin almacenamiento: no se recuerda */
        }
      };
      binding.current = state;
      main?.addEventListener("scroll", state.remember, { passive: true });
      try {
        main?.scrollTo({ top: Number(sessionStorage.getItem(key) ?? 0), behavior: "instant" });
      } catch {
        /* sin almacenamiento */
      }
    } else if (binding.current.key !== key) {
      binding.current.remember();
      binding.current.key = key;
      binding.current.main?.scrollTo({ top: 0, behavior: "instant" });
    }
    if (location.href !== url.href) history.replaceState(history.state, "", url);
  }, [enabled, search, page, serialized, status, role, ui.organizationId, ui.userId]);
  useEffect(
    () => () => {
      const state = binding.current;
      if (!state) return;
      state.remember();
      state.main?.removeEventListener("scroll", state.remember);
      binding.current = null;
    },
    [],
  );
}

/** El registro recién creado queda en la dirección sin navegar (OperatingListState.replaceUrl). */
export function replaceUrl(url: string): void {
  const target = new URL(url, location.href);
  if (target.origin === location.origin && location.href !== target.href)
    history.replaceState(history.state, "", target);
}

/** Lo que una lista de Equipo lee de la dirección al abrir. */
export function readQuery(): URLSearchParams {
  return new URLSearchParams(location.search);
}
export const pageOf = (query: URLSearchParams) => {
  const value = Number.parseInt(query.get("p") ?? "", 10);
  return Number.isFinite(value) ? Math.max(1, value) : 1;
};
